import * as Sentry from "@sentry/browser";
import { use as i18nextUse } from "i18next";
import { Settings } from "luxon";
import { createRoot } from "react-dom/client";
import { initReactI18next } from "react-i18next";
import { BrowserRouter } from "react-router";
// This is necessary for the AdminDashboard to work.
import { AdminDashboard } from "./components/AdminDashboard/AdminDashboard";
import { App } from "./components/App/App";
import de from "./i18n/translations/de.json";
import { shouldDropSentryEvent } from "./utils/shouldDropSentryEvent";
import "./scss/index.scss";

declare const SENTRY_DSN: string;

// This is necessary for the AdminDashboard to work.
declare global {
  interface Window {
    ReactComponents: {
      AdminDashboard: typeof AdminDashboard;
    };
  }
}

window.ReactComponents = {
  AdminDashboard
};

// Send uncaught exceptions to Sentry (https://docs.sentry.io/platforms/javascript/react/)
Sentry.init({
  // The SENTRY_DSN constant is defined using define in vite.config.mts
  dsn: SENTRY_DSN,
  ignoreErrors: [
    // Ignore backend-related errors (status 5xx)
    // because they will have a Sentry entry already
    "Request failed with status code 5"
  ],
  beforeBreadcrumb(breadcrumb, hint) {
    if (breadcrumb.category === "ui.click" && hint) {
      const { target } = hint.event;
      if (
        target instanceof HTMLElement &&
        target.className.includes("mantine-Button-")
      ) {
        breadcrumb.message = `Button clicked: ${target.innerText} ${target.ariaLabel ? ` (aria-label: ${target.ariaLabel})` : ""}; ${breadcrumb.message}`;
      }
    }
    return breadcrumb;
  },
  beforeSend(event) {
    return shouldDropSentryEvent(event) ? null : event;
  }
});

// Warning when switching languages: https://github.com/i18next/react-i18next/issues/1042
i18nextUse(initReactI18next).init({
  resources: {
    de: {
      translation: de
    }
  },
  lng: "de",
  fallbackLng: ["de"],
  interpolation: {
    escapeValue: false
  }
});

Settings.defaultLocale = "de";

const container = document.getElementById("root");

if (container) {
  const root = createRoot(container);
  root.render(
    <BrowserRouter>
      <App />
    </BrowserRouter>
  );
}
